import { useState, useEffect, useCallback } from 'react';
import { Spinner } from '../ui';
import { useWebshopStore } from '../../stores';
import { findSupplierForItems } from '../../config/suppliers';
import { AanFieldPicker } from '../inbox/AanFieldPicker';
import type { WebshopOrder } from '../../lib/salesforce';

interface ForwardOrderDialogProps {
  order: WebshopOrder | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

/**
 * Items die naar de leverancier gaan. Bij een gemengde order zijn dat alleen
 * de dropship-regels (de SEBO-items) — niet de eigen-verzending-items die wij
 * zelf opsturen. Valt terug op alle niet-verzendkosten-regels wanneer geen
 * enkele regel als dropship gemarkeerd is (legacy volledige Dropship-order).
 */
function getForwardItems(order: WebshopOrder) {
  const dropship = order.items.filter((i) => i.dropship);
  if (dropship.length > 0) return dropship;
  return order.items.filter((i) => !i.name.toLowerCase().includes('verzendkosten'));
}

function buildOrderText(order: WebshopOrder): string {
  const items = getForwardItems(order);
  const address = [order.street, [order.postalCode, order.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const total = items.reduce((sum, i) => sum + i.price, 0);

  return [
    `Bestelling: ${order.name}`,
    `Klant: ${order.customerName}`,
    address ? `Adres: ${address}` : '',
    order.customerPhone ? `Tel: ${order.customerPhone}` : '',
    order.customerEmail ? `Email: ${order.customerEmail}` : '',
    '',
    'Producten:',
    ...items.map((i) => `  ${i.quantity}x ${i.name} — ${formatCurrency(i.price)}`),
    '',
    `Totaal: ${formatCurrency(total)}`,
    order.notes ? `\nOpmerking: ${order.notes}` : '',
    '',
    'Graag rechtstreeks verzenden naar bovenstaand klantadres.',
  ].filter(Boolean).join('\n');
}

export function ForwardOrderDialog({ order, isOpen, onClose }: ForwardOrderDialogProps) {
  const { forwardOrder, isUpdating } = useWebshopStore();

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Pre-fill fields when order changes
  useEffect(() => {
    if (!order || !isOpen) return;

    const itemNames = getForwardItems(order).map((i) => i.name);
    const supplier = findSupplierForItems(itemNames);

    setTo(supplier?.email || '');
    setSubject(`Dropship bestelling ${order.name} — ${order.customerName}`);
    setBody(buildOrderText(order));
    setError(null);
    setSent(false);
  }, [order, isOpen]);

  const handleSend = useCallback(async () => {
    if (!order || !to.trim()) return;
    setError(null);

    try {
      await forwardOrder(order.id, to.trim(), subject, body);
      setSent(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verzenden mislukt');
    }
  }, [order, to, subject, body, forwardOrder, onClose]);

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Bestelling doorsturen naar leverancier
        </h3>

        {sent ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900">Verstuurd — order staat nu bij leverancier</p>
          </div>
        ) : (
          <>
            {/* To field */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Aan</label>
              {/* Voorgevuld met de leverancier; de picker laat je daarnaast in
                  het adresboek zoeken (partners, reparateurs, klanten). */}
              <AanFieldPicker variant="stacked" value={to} onChange={setTo} />
            </div>

            {/* Subject field */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Onderwerp</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Body textarea */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bericht</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-y"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={isUpdating || !to.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <Spinner className="w-4 h-4" />
                    Verzenden...
                  </>
                ) : (
                  'Verstuur'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
